import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  MapPin,
  Phone,
  Mail,
  Building,
  CreditCard,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Loader,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// DIRECT: Use BuyerController API service (ONLY correct service)
import * as buyerAPI from '@/services/buyerAPI';
import type { Buyer, CreateBuyerRequest, AddressDto } from '@/services/buyerAPI';

// Order-related imports
import { microservicesClient } from '@/services/microservices';
import { useSalesUnitFilter } from '@/hooks/useSalesUnitFilter';

type Customer = Buyer & {
  totalOrders?: number;
  totalRevenue?: number;
};
type CustomerFilters = any;

interface CustomerCardProps {
  customer: Customer;
  onEdit: (customer: Customer) => void;
  onViewDetails: (customer: Customer) => void;
}

interface Order {
  id: number;
  orderNumber: string;
  customerName: string;
  status: string;
  createdAt: string;
  finalAmount: number;
  orderDate?: string;
  [key: string]: any;
}

/**
 * OPTIMIZED: Fetch customers with metrics in a SINGLE API call
 * This replaces the N+1 query problem (1 + 50+ calls) with just 1 call
 *
 * OLD APPROACH:
 * - 1 query to get customers
 * - Then for EACH customer, fetch orders separately (N queries)
 * - Total: 1 + N = 51+ API calls for 50 customers
 *
 * NEW APPROACH:
 * - 1 single query with LEFT JOIN to orders and GROUP BY aggregation
 * - Includes total_orders and total_revenue in the response
 * - Total: 1 API call
 *
 * Performance improvement: 50x reduction in API calls!
 */
async function fetchCustomersWithMetrics(page: number = 0, size: number = 100, salesUnitIds?: number[]): Promise<{
  customers: Customer[];
  totalCustomers: number;
  totalPages: number;
}> {
  try {
    console.log(`✨ Fetching customers with metrics (OPTIMIZED - 1 API CALL) - page: ${page}, size: ${size}, salesUnitIds: ${salesUnitIds}`);

    // Build params, include salesUnitIds if provided
    const params: Record<string, any> = { page, size };
    if (salesUnitIds && salesUnitIds.length > 0) {
      params.salesUnitIds = salesUnitIds.join(',');
    }

    // Call the new optimized endpoint
    const response = await microservicesClient.get('commerce-service', '/api/v1/customers/with-metrics', {
      params
    });

    console.log(`✨ Optimized API response:`, {
      success: response?.success,
      customerCount: response?.data?.content?.length,
      totalCustomers: response?.data?.totalElements,
      totalPages: response?.data?.totalPages
    });

    if (response?.success && response?.data?.content && Array.isArray(response.data.content)) {
      const customers = (response.data.content as any[]).map((dto: any) => {
        // Parse registeredAddress string into address object for edit form compatibility
        // Format: "street, city, state - pincode, country"
        let addressObj: any = { street: '', city: '', state: '', pincode: '', country: 'India' };
        if (dto.registeredAddress && typeof dto.registeredAddress === 'string') {
          const raw = dto.registeredAddress.trim();
          const parts = raw.split(',').map((s: string) => s.trim());

          if (raw.includes(' - ') && parts.length >= 3) {
            // Format from frontend update: "street, city, state - pincode, country"
            addressObj.street = parts[0] || '';
            addressObj.city = parts[1] || '';
            const statePin = parts[2] || '';
            const statePinParts = statePin.split(' - ').map((s: string) => s.trim());
            addressObj.state = statePinParts[0] || '';
            addressObj.pincode = statePinParts[1] || '';
            addressObj.country = parts[3] || 'India';
          } else if (parts.length >= 3) {
            // Format from backend create: "street, city, state pincode"
            addressObj.street = parts[0] || '';
            addressObj.city = parts[1] || '';
            const lastPart = parts[parts.length - 1] || '';
            const pincodeMatch = lastPart.match(/(\d{6})$/);
            if (pincodeMatch) {
              addressObj.pincode = pincodeMatch[1];
              addressObj.state = lastPart.replace(pincodeMatch[1], '').trim();
            } else {
              addressObj.state = lastPart;
            }
          } else if (parts.length === 2) {
            addressObj.street = parts[0] || '';
            addressObj.city = parts[1] || '';
          } else {
            addressObj.street = raw;
          }
        }

        // Map backend paymentTerms enum to frontend days number
        const paymentTermsDays = dto.paymentTerms === 'ADVANCE' ? 0
          : dto.paymentTerms === 'NET_30' ? 30
          : dto.paymentTerms === 'NET_45' ? 45
          : dto.paymentTerms === 'NET_60' ? 60
          : 30;

        return {
          id: dto.id,
          name: dto.companyName,
          type: dto.buyerType,
          contactPerson: dto.contactPersonName,
          contactPersonDesignation: dto.contactPersonDesignation,
          phone: dto.primaryPhone,
          email: dto.email,
          address: addressObj,
          gstNumber: dto.gstNumber || '',
          panNumber: dto.panNumber || '',
          paymentTerms: paymentTermsDays,
          status: (dto.contractStatus === 'ACTIVE' || dto.onboardingStatus === 'APPROVED') ? 'active' : 'inactive',
          contractStatus: dto.contractStatus,
          onboardingStatus: dto.onboardingStatus,
          totalOrders: dto.totalOrders || 0,
          totalRevenue: dto.totalRevenue || 0,
          creditLimit: dto.creditLimit,
          creditUsed: dto.creditUsed
        };
      });

      console.log(`✅ Successfully fetched ${customers.length} customers with all metrics in 1 API call!`);

      return {
        customers,
        totalCustomers: response.data.totalElements || 0,
        totalPages: response.data.totalPages || 0
      };
    } else {
      console.warn('⚠️ No valid response data received');
      return { customers: [], totalCustomers: 0, totalPages: 0 };
    }
  } catch (error) {
    console.error(`❌ Failed to fetch customers with metrics:`, error);
    console.error(`❌ Error details:`, {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    return { customers: [], totalCustomers: 0, totalPages: 0 };
  }
}

const CustomerCard: React.FC<CustomerCardProps> = ({ customer, onEdit, onViewDetails }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'COMMERCIAL':
        return 'Commercial';
      case 'INDUSTRIAL':
        return 'Industrial';
      case 'RESIDENTIAL':
        return 'Residential';
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'COMMERCIAL':
        return 'bg-blue-100 text-blue-800';
      case 'INDUSTRIAL':
        return 'bg-purple-100 text-purple-800';
      case 'RESIDENTIAL':
        return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0 flex-1">
            <CardTitle className="text-lg truncate">{customer.name}</CardTitle>
            <div className="flex items-center flex-wrap gap-1">
              <Badge className={getStatusColor(customer.status)}>{customer.status}</Badge>
              <Badge className={getTypeColor(customer.type)}>{getTypeLabel(customer.type)}</Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Contact Information */}
        <div className="space-y-2 min-w-0">
          <div className="flex items-center text-sm text-gray-600 min-w-0">
            <Building className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{customer.contactPerson}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600 min-w-0">
            <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{customer.phone}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600 min-w-0">
            <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{customer.email}</span>
          </div>
          <div className="flex items-start text-sm text-gray-600 min-w-0">
            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0 break-words">
              {typeof customer.address === 'string'
                ? customer.address
                : customer.address && customer.address.city
                  ? `${customer.address.street || ''}, ${customer.address.city}, ${customer.address.state} - ${customer.address.pincode || ''}`.replace(/^,\s+/, '')
                  : 'Address not available'}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div className="text-center min-w-0">
            <div className="text-lg font-semibold truncate">{customer.totalOrders || 0}</div>
            <div className="text-xs text-gray-500">Total Orders</div>
          </div>
          <div className="text-center min-w-0">
            <div className="text-lg font-semibold truncate">
              {customer.totalRevenue && customer.totalRevenue > 0
                ? customer.totalRevenue >= 100000
                  ? `₹${(customer.totalRevenue / 100000).toFixed(2)}L`
                  : customer.totalRevenue >= 1000
                    ? `₹${(customer.totalRevenue / 1000).toFixed(1)}K`
                    : `₹${customer.totalRevenue.toFixed(2)}`
                : '₹0'}
            </div>
            <div className="text-xs text-gray-500">Revenue</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2 pt-2">
          <Button variant="outline" size="sm" className="flex-1 min-w-0" onClick={() => onViewDetails(customer)}>
            View Details
          </Button>
          <Button variant="outline" size="sm" className="flex-1 min-w-0" onClick={() => onEdit(customer)}>
            <Edit className="w-4 h-4 mr-1 flex-shrink-0" />
            Edit
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

interface CustomerFormProps {
  customer?: Customer;
  onSave: (customer: Customer) => void;
  onCancel: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ customer, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Partial<Customer>>(
    customer || {
      name: '',
      type: 'COMMERCIAL',
      contactPerson: '',
      phone: '',
      email: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      paymentTerms: 30,
      gstNumber: '',
      panNumber: '',
      status: 'inactive',
      contractStatus: 'INACTIVE'
    }
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sync form data when customer prop changes (important for pre-fill from inquiry)
  useEffect(() => {
    if (customer) {
      console.log('Syncing form data with customer prop:', customer);
      setFormData(customer);
    }
  }, [customer]);

  // Validate form fields and return errors
  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Company/Customer name is required';
    }
    if (!formData.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }

    // Phone validation: exactly 10 digits starting with 6-9
    const phoneDigits = (formData.phone || '').replace(/^\+91/, '');
    if (!phoneDigits) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9][0-9]{9}$/.test(phoneDigits)) {
      newErrors.phone = 'Enter a valid 10-digit mobile number starting with 6-9';
    }

    // Email validation: must start with an alphabet, alphanumeric body, end with @gmail.com
    const emailValue = (formData.email || '').trim();
    if (!emailValue) {
      newErrors.email = 'Email address is required';
    } else if (/^[0-9]/.test(emailValue)) {
      newErrors.email = 'Email must start with an alphabet, not a number';
    } else if (!/^[a-zA-Z][a-zA-Z0-9._-]*@gmail\.com$/.test(emailValue)) {
      newErrors.email = 'Enter a valid email ending with @gmail.com (e.g., name123@gmail.com)';
    }

    if (!formData.address?.street?.trim()) {
      newErrors.street = 'Street address is required';
    }
    if (!formData.address?.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.address.city.trim())) {
      newErrors.city = 'City name must contain only alphabets';
    }
    if (!formData.address?.state?.trim()) {
      newErrors.state = 'State is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.address.state.trim())) {
      newErrors.state = 'State name must contain only alphabets';
    }
    if (!formData.address?.pincode?.trim()) {
      newErrors.pincode = 'PIN code is required';
    } else if (!/^[0-9]{6}$/.test(formData.address.pincode.trim())) {
      newErrors.pincode = 'PIN code must be exactly 6 digits';
    }
    if (!formData.address?.country?.trim()) {
      newErrors.country = 'Country is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.address.country.trim())) {
      newErrors.country = 'Country name must contain only alphabets';
    }

    const paymentTermsValue = formData.paymentTerms;
    const allowedPaymentTerms = [0, 30, 45, 60];
    if (paymentTermsValue === undefined || paymentTermsValue === null || (paymentTermsValue as any) === '' || Number.isNaN(Number(paymentTermsValue))) {
      newErrors.paymentTerms = 'Payment terms is required';
    } else if (!allowedPaymentTerms.includes(Number(paymentTermsValue))) {
      newErrors.paymentTerms = 'Select a valid payment term (Advance, Net 30, Net 45, or Net 60)';
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Run custom validation
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const el = document.getElementById(firstErrorField === 'street' ? 'street' : firstErrorField);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData as Customer);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to render required asterisk
  const RequiredMark = () => <span className="text-red-500 ml-0.5">*</span>;

  // Helper to render field error
  const FieldError = ({ field }: { field: string }) => {
    if (!errors[field]) return null;
    return <p className="text-sm text-red-500 mt-1">{errors[field]}</p>;
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Company/Customer Name <RequiredMark /></Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (errors.name) setErrors({ ...errors, name: '' });
            }}
            className={errors.name ? 'border-red-500' : ''}
            placeholder="Enter company or customer name"
          />
          <FieldError field="name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="type">Customer Type <RequiredMark /></Label>
          <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as any })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="COMMERCIAL">Commercial</SelectItem>
              <SelectItem value="INDUSTRIAL">Industrial</SelectItem>
              <SelectItem value="RESIDENTIAL">Residential</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contactPerson">Contact Person <RequiredMark /></Label>
          <Input
            id="contactPerson"
            value={formData.contactPerson}
            onChange={(e) => {
              // Only allow alphabets, spaces, and dots (for names like "Dr. Rajesh Kumar")
              const filtered = e.target.value.replace(/[^a-zA-Z\s.]/g, '');
              setFormData({ ...formData, contactPerson: filtered });
              if (errors.contactPerson) setErrors({ ...errors, contactPerson: '' });
            }}
            className={errors.contactPerson ? 'border-red-500' : ''}
            placeholder="Enter contact person name"
          />
          <FieldError field="contactPerson" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number <RequiredMark /></Label>
          <div className="relative flex items-center">
            <span className="absolute left-3 text-sm text-muted-foreground font-medium select-none">+91</span>
            <Input
              id="phone"
              type="text"
              inputMode="numeric"
              maxLength={10}
              value={(formData.phone || '').replace(/^\+91/, '')}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                setFormData({ ...formData, phone: digits ? '+91' + digits : '' });
                if (errors.phone) setErrors({ ...errors, phone: '' });
              }}
              className={`pl-12 ${errors.phone ? 'border-red-500' : ''}`}
              placeholder="9876543210"
            />
          </div>
          <FieldError field="phone" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email Address <RequiredMark /></Label>
        <Input
          id="email"
          type="text"
          value={formData.email}
          onChange={(e) => {
            let value = e.target.value.replace(/\s/g, '').toLowerCase();
            // Disallow starting with a digit or non-alphabet character
            if (value.length > 0 && !/^[a-z]/.test(value)) {
              value = value.replace(/^[^a-z]+/, '');
            }
            // Allow only alphanumerics and . _ - before the @, then @gmail.com characters
            value = value.replace(/[^a-z0-9._@-]/g, '');
            // Ensure only a single '@'
            const atIndex = value.indexOf('@');
            if (atIndex !== -1) {
              value = value.slice(0, atIndex + 1) + value.slice(atIndex + 1).replace(/@/g, '');
            }
            setFormData({ ...formData, email: value });
            if (errors.email) setErrors({ ...errors, email: '' });
          }}
          className={errors.email ? 'border-red-500' : ''}
          placeholder="name123@gmail.com"
        />
        <p className="text-xs text-muted-foreground">
          Must start with an alphabet, may contain letters/numbers, and must end with @gmail.com
        </p>
        <FieldError field="email" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="street">Street Address <RequiredMark /></Label>
        <Textarea
          id="street"
          value={formData.address?.street}
          onChange={(e) => {
            setFormData({
              ...formData,
              address: { ...formData.address!, street: e.target.value }
            });
            if (errors.street) setErrors({ ...errors, street: '' });
          }}
          className={errors.street ? 'border-red-500' : ''}
          placeholder="Enter street address"
        />
        <FieldError field="street" />
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City <RequiredMark /></Label>
          <Input
            id="city"
            type="text"
            inputMode="text"
            value={formData.address?.city}
            onChange={(e) => {
              const alphaOnly = e.target.value.replace(/[^a-zA-Z\s]/g, '');
              setFormData({
                ...formData,
                address: { ...formData.address!, city: alphaOnly }
              });
              if (errors.city) setErrors({ ...errors, city: '' });
            }}
            onKeyDown={(e) => {
              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
              if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
              if (!/^[a-zA-Z\s]$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className={errors.city ? 'border-red-500' : ''}
            placeholder="City (alphabets only)"
          />
          <FieldError field="city" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State <RequiredMark /></Label>
          <Input
            id="state"
            type="text"
            inputMode="text"
            value={formData.address?.state}
            onChange={(e) => {
              const alphaOnly = e.target.value.replace(/[^a-zA-Z\s]/g, '');
              setFormData({
                ...formData,
                address: { ...formData.address!, state: alphaOnly }
              });
              if (errors.state) setErrors({ ...errors, state: '' });
            }}
            onKeyDown={(e) => {
              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
              if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
              if (!/^[a-zA-Z\s]$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className={errors.state ? 'border-red-500' : ''}
            placeholder="State (alphabets only)"
          />
          <FieldError field="state" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pincode">PIN Code <RequiredMark /></Label>
          <Input
            id="pincode"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={formData.address?.pincode}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
              setFormData({
                ...formData,
                address: { ...formData.address!, pincode: digits }
              });
              if (errors.pincode) setErrors({ ...errors, pincode: '' });
            }}
            className={errors.pincode ? 'border-red-500' : ''}
            placeholder="500001"
          />
          <FieldError field="pincode" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">Country <RequiredMark /></Label>
          <Input
            id="country"
            type="text"
            inputMode="text"
            value={formData.address?.country}
            onChange={(e) => {
              const alphaOnly = e.target.value.replace(/[^a-zA-Z\s]/g, '');
              setFormData({
                ...formData,
                address: { ...formData.address!, country: alphaOnly }
              });
              if (errors.country) setErrors({ ...errors, country: '' });
            }}
            onKeyDown={(e) => {
              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
              if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
              if (!/^[a-zA-Z\s]$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className={errors.country ? 'border-red-500' : ''}
            placeholder="Country (alphabets only)"
          />
          <FieldError field="country" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="paymentTerms">Payment Terms <RequiredMark /></Label>
          <Select
            value={formData.paymentTerms !== undefined && formData.paymentTerms !== null ? String(formData.paymentTerms) : '30'}
            onValueChange={(value) => {
              setFormData({ ...formData, paymentTerms: Number(value) });
              if (errors.paymentTerms) setErrors({ ...errors, paymentTerms: '' });
            }}
          >
            <SelectTrigger id="paymentTerms" className={errors.paymentTerms ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select payment terms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Advance Payment</SelectItem>
              <SelectItem value="30">Net 30 Days</SelectItem>
              <SelectItem value="45">Net 45 Days</SelectItem>
              <SelectItem value="60">Net 60 Days</SelectItem>
            </SelectContent>
          </Select>
          <FieldError field="paymentTerms" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="customerStatus">Customer Status</Label>
          <Select
            value={formData.contractStatus || 'INACTIVE'}
            onValueChange={(value) => setFormData({
              ...formData,
              contractStatus: value,
              status: value === 'ACTIVE' ? 'active' : value === 'SUSPENDED' ? 'suspended' : 'inactive'
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Set customer as Active or Inactive</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="gstNumber">GST Number (Optional)</Label>
          <Input
            id="gstNumber"
            value={formData.gstNumber}
            onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
            placeholder="e.g., 22AAAAA0000A1Z5"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="panNumber">PAN Number (Optional)</Label>
          <Input
            id="panNumber"
            value={formData.panNumber}
            onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
            placeholder="e.g., AAAAA0000A"
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              {(customer && customer.id > 0) ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            (customer && customer.id > 0) ? 'Update Customer' : 'Create Customer'
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

const CustomerManagement: React.FC = () => {
  // Sales unit filtering - only show customers from user's assigned sales units
  const { salesUnitIds, isLoading: salesUnitLoading, shouldFilter } = useSalesUnitFilter();

  // URL params for return-to-order flow and inquiry pre-fill
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = searchParams.get('returnTo');
  const openForm = searchParams.get('openForm') === 'true';

  // Extract inquiry parameters for pre-filling customer form
  const inquiryId = searchParams.get('inquiryId');
  const inquiryCustomerName = searchParams.get('customerName');
  const inquiryCustomerPhone = searchParams.get('customerPhone');
  const inquiryCustomerEmail = searchParams.get('customerEmail');
  const inquiryLocation = searchParams.get('location');

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<CustomerFilters>({});
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerOrders, setCustomerOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  // Don't initialize form open state immediately - wait for customer data to be set
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);

  // Pre-fill customer form from inquiry parameters (on mount)
  useEffect(() => {
    if (openForm && inquiryCustomerName && inquiryCustomerPhone) {
      console.log('🔄 Pre-filling customer form with inquiry data:', {
        name: inquiryCustomerName,
        phone: inquiryCustomerPhone,
        email: inquiryCustomerEmail,
        location: inquiryLocation,
      });

      // Create a pre-filled customer object from inquiry data
      const preFilled = {
        id: 0, // Temporary ID for new customer
        externalId: '',
        type: 'COMMERCIAL', // Default type
        name: inquiryCustomerName,
        contactPerson: inquiryCustomerName, // Use customer name as contact person
        phone: inquiryCustomerPhone,
        email: inquiryCustomerEmail || '',
        registeredAddress: inquiryLocation || '',
        address: {
          street: inquiryLocation || '',
          city: '',
          state: '',
          pincode: '',
          country: 'India',
        } as AddressDto,
        paymentTerms: '30',
        contractStatus: 'INACTIVE',
        onboardingStatus: 'PENDING',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Customer;

      setSelectedCustomer(preFilled);
      // Open form AFTER setting customer data
      setShowCustomerForm(true);

      console.log('✅ Customer form pre-filled and opened');
    }
  }, [openForm, inquiryCustomerName, inquiryCustomerPhone, inquiryCustomerEmail, inquiryLocation]);

  // Handle back to order creation
  const handleBackToOrder = (newCustomerId?: number) => {
    console.log('🔄 handleBackToOrder called with:', { newCustomerId, returnTo, inquiryId });

    if (returnTo) {
      let decodedReturnUrl = decodeURIComponent(returnTo);
      console.log('🔄 Decoded return URL:', decodedReturnUrl);

      // If a new customer was created from inquiry, append customer ID and inquiry params
      if (newCustomerId && inquiryId) {
        const params = new URLSearchParams({
          newCustomerId: newCustomerId.toString(),
          inquiryId: inquiryId,
          customerName: inquiryCustomerName || '',
          customerPhone: inquiryCustomerPhone || '',
          ...(inquiryCustomerEmail && { customerEmail: inquiryCustomerEmail }),
          ...(searchParams.get('productName') && { productName: searchParams.get('productName')! }),
          ...(searchParams.get('quantity') && { quantity: searchParams.get('quantity')! }),
          ...(searchParams.get('quantityUnit') && { quantityUnit: searchParams.get('quantityUnit')! }),
          ...(inquiryLocation && { location: inquiryLocation }),
        });
        decodedReturnUrl += `?${params.toString()}`;
        console.log('🔄 Final URL with params:', decodedReturnUrl);
      } else {
        console.log('⚠️ Missing newCustomerId or inquiryId, navigating without params');
      }

      console.log('🔄 Navigating to:', decodedReturnUrl);
      navigate(decodedReturnUrl);
    } else {
      console.log('⚠️ No returnTo URL found');
    }
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      console.log('🚀 OPTIMIZED LOAD: Using single API call with /with-metrics endpoint');
      console.log('📡 API Endpoint: GET /commerce-service/api/v1/customers/with-metrics');

      // ✨ OPTIMIZED APPROACH: Single API call gets all customers with aggregated metrics!
      // This replaces the N+1 pattern (51+ calls) with just 1 call
      const { customers: customersWithMetrics, totalCustomers, totalPages } = await fetchCustomersWithMetrics(
        0, 100, shouldFilter ? salesUnitIds : undefined
      );

      console.log(`✨ SUCCESS: Fetched ${customersWithMetrics.length} customers with all metrics in 1 API call!`);

      if (customersWithMetrics && customersWithMetrics.length > 0) {
        console.log('✅ Successfully loaded', customersWithMetrics.length, 'customers from database');

        // Transform and display all customers with metrics (they already have totalOrders and totalRevenue)
        // Spread all fields so paymentTerms, gstNumber, panNumber etc. are preserved for edit form
        const transformedWithMetrics = customersWithMetrics.map(customer => ({
          ...customer,
          totalOrders: customer.totalOrders || 0,
          totalRevenue: customer.totalRevenue || 0,
        }));

        setCustomers(transformedWithMetrics);
        setFilteredCustomers(transformedWithMetrics);

        console.log(`✅ Successfully loaded ${transformedWithMetrics.length} customers with metrics`);
        console.log(`📊 Total: ${totalCustomers}, Pages: ${totalPages}`);
        toast.success(`✅ Loaded ${transformedWithMetrics.length} customers with metrics (1 API call!)`);
      } else {
        console.warn('⚠️ No customers found in database response');
        setCustomers([]);
        setFilteredCustomers([]);
        toast.info('No customers found in the database. Click "Add Customer" to create one.');
      }
    } catch (error: any) {
      console.error('❌ CRITICAL ERROR - Full Error Object:');
      console.error('Error:', error);
      console.error('Error message:', error?.message);
      console.error('Error status:', error?.response?.status);
      console.error('Error data:', error?.response?.data);
      console.error('Error stack:', error?.stack);

      setCustomers([]);
      setFilteredCustomers([]);

      // Get specific error message
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch customers from database API';

      toast.error(`❌ Failed to load customers: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!salesUnitLoading) {
      loadCustomers();
    }
  }, [filters, salesUnitLoading, salesUnitIds]);

  useEffect(() => {
    const searchLower = searchTerm.toLowerCase();

    let filtered = [...customers];

    // Apply status filter
    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter(customer =>
        filters.status.includes(customer.status)
      );
      console.log(`Status filter "${filters.status}" applied, ${filtered.length} customers remaining`);
    }

    // Apply type filter
    if (filters.type && filters.type.length > 0) {
      filtered = filtered.filter(customer =>
        filters.type.includes(customer.type)
      );
      console.log(`Type filter "${filters.type}" applied, ${filtered.length} customers remaining`);
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(customer => {
        // Search in name
        if (customer.name && customer.name.toLowerCase().includes(searchLower)) return true;

        // Search in contact person
        if (customer.contactPerson && customer.contactPerson.toLowerCase().includes(searchLower)) return true;

        // Search in email
        if (customer.email && customer.email.toLowerCase().includes(searchLower)) return true;

        // Search in phone (exact and case-insensitive)
        if (customer.phone && customer.phone.toLowerCase().includes(searchLower)) return true;

        // Search in registered address (can be string or object)
        const addrStr = typeof customer.address === 'string'
          ? customer.address
          : customer.address
            ? `${customer.address.street || ''} ${customer.address.city || ''} ${customer.address.state || ''} ${customer.address.pincode || ''}`
            : '';
        if (addrStr && addrStr.toLowerCase().includes(searchLower)) return true;

        // Search in customer ID (convert to string first)
        if (customer.id && String(customer.id).toLowerCase().includes(searchLower)) return true;

        // Search in buyer code if available
        if (customer.buyerCode && customer.buyerCode.toLowerCase().includes(searchLower)) return true;

        return false;
      });
      console.log(`Search for "${searchTerm}" found ${filtered.length} customers`);
    }

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, filters]);

  const handleCreateCustomer = () => {
    setSelectedCustomer(null);
    setShowCustomerForm(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowCustomerForm(true);
  };

  const handleViewCustomerDetails = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowCustomerDetails(true);

    // Fetch detailed orders for this specific customer
    setLoadingOrders(true);
    try {
      console.log(`📦 Loading order details for customer: ${customer.name} (ID: ${customer.id})`);

      // Fetch orders for this specific customer
      const response = await microservicesClient.get('commerce-service', '/api/v1/orders', {
        params: {
          buyerId: customer.id,
          page: 0,
          size: 100,
          sortBy: 'createdAt',
          sortDir: 'desc'
        }
      });

      if (response?.success && response?.data?.content && Array.isArray(response.data.content)) {
        const orders = response.data.content as Order[];
        setCustomerOrders(orders);
        console.log(`✅ Loaded ${orders.length} orders for ${customer.name}`);
      } else {
        setCustomerOrders([]);
        console.warn('No orders found for customer');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setCustomerOrders([]);
      toast.error('Failed to load orders for this customer');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleSaveCustomer = async (customerData: Customer) => {
    const toastId = toast.loading(selectedCustomer ? 'Updating customer...' : 'Creating customer...');

    try {
      // Validate required fields
      if (!customerData.name?.trim()) {
        throw new Error('Company name is required');
      }
      if (!customerData.type) {
        throw new Error('Customer type (B2B or B2C) is required');
      }
      if (!customerData.contactPerson?.trim()) {
        throw new Error('Contact person name is required');
      }
      if (!customerData.phone?.trim()) {
        throw new Error('Phone number is required');
      }
      if (!customerData.email?.trim()) {
        throw new Error('Email address is required');
      }
      // Payment terms is required only for new customers; for updates, backend has a fallback
      if (!selectedCustomer && !customerData.paymentTerms) {
        throw new Error('Payment terms is required');
      }

      // Handle address - it can be nested object or string
      let street = '';
      let city = '';
      let state = '';
      let pincode = '';
      let country = 'India';

      if (typeof customerData.address === 'object' && customerData.address) {
        street = customerData.address.street?.trim() || '';
        city = customerData.address.city?.trim() || '';
        state = customerData.address.state?.trim() || '';
        pincode = customerData.address.pincode?.trim() || '';
        country = customerData.address.country?.trim() || 'India';
      }

      if (!street || !city || !state || !pincode) {
        throw new Error('Complete address (street, city, state, PIN) is required');
      }

      // Check if this is an existing customer (id > 0) or a new customer (id === 0 or null)
      if (selectedCustomer && selectedCustomer.id > 0) {
        // Update existing customer
        console.log('📡 Updating customer ID:', selectedCustomer.id);
        // Transform frontend field names to backend field names
        // Backend BuyerUpdateRequest expects: companyName, contactPersonName, primaryPhone, registeredAddress
        const updateRequest: any = {
          buyerType: customerData.type,
          companyName: customerData.name.trim(),
          contactPersonName: customerData.contactPerson.trim(),
          primaryPhone: customerData.phone.trim(),
          email: customerData.email.trim(),
          gstNumber: customerData.gstNumber?.trim() || null,
          panNumber: customerData.panNumber?.trim() || null,
          // Convert address object to registeredAddress string
          registeredAddress: `${street}, ${city}, ${state} - ${pincode}, ${country}`,
          // Map payment terms from number of days to string enum
          paymentTerms: Number(customerData.paymentTerms) === 0 ? 'ADVANCE'
            : Number(customerData.paymentTerms) === 30 ? 'NET_30'
            : Number(customerData.paymentTerms) === 45 ? 'NET_45'
            : Number(customerData.paymentTerms) === 60 ? 'NET_60'
            : 'ADVANCE',
          // Include contract status for active/inactive toggling
          contractStatus: customerData.contractStatus || 'INACTIVE',
        };
        console.log('📡 Update request (transformed to backend format):', JSON.stringify(updateRequest, null, 2));
        await buyerAPI.updateBuyer(selectedCustomer.id, updateRequest as any);
        toast.success('✅ Customer updated successfully!', { id: toastId });
      } else {
        // Create NEW customer
        console.log('📡 Creating NEW customer via BuyerController API...');
        const createRequest: CreateBuyerRequest = {
          type: (customerData.type || 'COMMERCIAL') as 'COMMERCIAL' | 'INDUSTRIAL' | 'RESIDENTIAL',
          name: customerData.name.trim(),
          contactPerson: customerData.contactPerson.trim(),
          phone: customerData.phone.trim(),
          email: customerData.email.trim(),
          gstNumber: customerData.gstNumber?.trim(),
          panNumber: customerData.panNumber?.trim(),
          address: { street, city, state, pincode, country },
          creditLimit: 50000,
          paymentTerms: Number(customerData.paymentTerms) || 30,
          contractStatus: customerData.contractStatus || 'INACTIVE',
          onboardingStatus: 'INITIATED',
          // Assign to current user's sales unit for data isolation
          // Without this, non-admin users can't see the customer they just created
          salesUnitId: salesUnitIds.length > 0 ? salesUnitIds[0] : undefined,
        };

        console.log('📡 Create request:', JSON.stringify(createRequest, null, 2));
        const response = await buyerAPI.createBuyer(createRequest);
        console.log('📡 Create response:', JSON.stringify(response, null, 2));

        // Extract customer ID from response - try multiple paths
        const newCustomerId = response.data?.data?.id || response.data?.id || response.id;
        console.log('📡 New customer ID:', newCustomerId);

        toast.success('Customer created successfully!', { id: toastId });

        // If returnTo is set, redirect back to order creation with new customer ID
        if (returnTo) {
          if (!newCustomerId) {
            console.error('Failed to extract customer ID from response:', response);
            toast.error('Customer created but failed to get ID. Please select manually.');
          } else {
            handleBackToOrder(newCustomerId);
            return; // Exit early, don't reload customers
          }
        }
      }

      setShowCustomerForm(false);

      // Clear search and filters so the new/updated customer is always visible
      setSearchTerm('');
      setFilters({});

      // Small delay to ensure DB transaction is committed before fetching
      await new Promise(resolve => setTimeout(resolve, 300));

      // Reload customers list to show new/updated customer
      await loadCustomers();
    } catch (error: any) {
      console.error('❌ Error:', error);
      console.error('Error response:', error?.response?.data);

      // Extract error message
      let errorMessage = error?.message || 'Failed to save customer';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      toast.error(`❌ ${errorMessage}`, { id: toastId });
    }
  };

  const getCustomerStats = () => {
    const total = customers.length;
    // Use onboardingStatus and contractStatus from Buyer entity instead
    const active = customers.filter(c => c.contractStatus === 'ACTIVE' || c.onboardingStatus === 'APPROVED').length;

    return { total, active, highRisk: 0, totalRevenue: 0 };
  };

  const stats = getCustomerStats();

  return (
    <div className="space-y-6">
      {/* Back to Order Button (when coming from order creation) */}
      {returnTo && (
        <Button
          variant="outline"
          onClick={handleBackToOrder}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Order Creation
        </Button>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Customer Management</h1>
          <p className="text-gray-600">
            {returnTo
              ? 'Create a new customer, then return to your order'
              : 'Manage customer profiles, credit limits, and relationships'}
          </p>
        </div>
        <Button onClick={handleCreateCustomer}>
          <Plus className="w-4 h-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search customers by name, contact, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s@.\-]/g, '');
                  setSearchTerm(sanitized);
                }}
                maxLength={50}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filters.status?.[0] || 'all'} onValueChange={(value) =>
                setFilters({ ...filters, status: value === 'all' ? undefined : [value] })
              }>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.type?.[0] || 'all'} onValueChange={(value) =>
                setFilters({ ...filters, type: value === 'all' ? undefined : [value] })
              }>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="COMMERCIAL">Commercial</SelectItem>
                  <SelectItem value="INDUSTRIAL">Industrial</SelectItem>
                  <SelectItem value="RESIDENTIAL">Residential</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadCustomers()}
                disabled={loading}
                title="Refresh customer list"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
          <p className="text-gray-600">Loading customers...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Customers Found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || (filters.status?.length || filters.type?.length)
                ? 'Try adjusting your search or filters to find customers.'
                : 'No customers created yet. Click "Add Customer" to get started.'}
            </p>
            {(searchTerm || filters.status?.length || filters.type?.length) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilters({});
                }}
              >
                Clear Filters & Search
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredCustomers.length}</span> of{' '}
              <span className="font-semibold">{customers.length}</span> customers
              {searchTerm && <span className="text-blue-600"> (search: "{searchTerm}")</span>}
            </p>
            {(filters.status?.length || filters.type?.length) && (
              <div className="flex gap-2">
                {filters.status && (
                  <Badge variant="secondary">
                    Status: {filters.status.join(', ')}
                  </Badge>
                )}
                {filters.type && (
                  <Badge variant="secondary">
                    Type: {filters.type.join(', ')}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCustomers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onEdit={handleEditCustomer}
                onViewDetails={handleViewCustomerDetails}
              />
            ))}
          </div>
        </>
      )}

      {/* Customer Form Dialog */}
      <Dialog open={showCustomerForm} onOpenChange={setShowCustomerForm}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
            <DialogDescription>
              {selectedCustomer ? 'Update customer information and settings.' : 'Create a new customer profile with contact and credit information.'}
            </DialogDescription>
          </DialogHeader>
          <CustomerForm
            customer={selectedCustomer || undefined}
            onSave={handleSaveCustomer}
            onCancel={() => setShowCustomerForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Customer Details Dialog */}
      <Dialog open={showCustomerDetails} onOpenChange={setShowCustomerDetails}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCustomer?.name}</DialogTitle>
            <DialogDescription>Complete customer profile and history</DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <Tabs defaultValue="profile" className="w-full">
              <TabsList>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="orders">Orders</TabsTrigger>
              </TabsList>
              <TabsContent value="profile" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div><strong>Contact Person:</strong> {selectedCustomer.contactPerson}</div>
                      <div><strong>Phone:</strong> {selectedCustomer.phone}</div>
                      <div><strong>Email:</strong> {selectedCustomer.email}</div>
                      <div><strong>Address:</strong> {typeof selectedCustomer.address === 'string'
                        ? selectedCustomer.address
                        : selectedCustomer.address
                          ? `${selectedCustomer.address.street || ''}, ${selectedCustomer.address.city || ''}, ${selectedCustomer.address.state || ''} - ${selectedCustomer.address.pincode || ''}, ${selectedCustomer.address.country || 'India'}`
                          : 'N/A'}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Business Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div><strong>Type:</strong> {selectedCustomer.buyerType || 'N/A'}</div>
                      <div><strong>GST Number:</strong> {selectedCustomer.gstNumber || 'N/A'}</div>
                      <div><strong>PAN Number:</strong> {selectedCustomer.panNumber || 'N/A'}</div>
                      <div><strong>Payment Terms:</strong> {
                        selectedCustomer.paymentTerms === 0 ? 'Advance Payment'
                          : selectedCustomer.paymentTerms === 30 ? 'Net 30 Days'
                          : selectedCustomer.paymentTerms === 45 ? 'Net 45 Days'
                          : selectedCustomer.paymentTerms === 60 ? 'Net 60 Days'
                          : 'N/A'
                      }</div>
                      <div><strong>Registration Date:</strong> {selectedCustomer.createdAt ? new Date(selectedCustomer.createdAt).toLocaleDateString() : 'N/A'}</div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              <TabsContent value="orders">
                <Card>
                  <CardHeader>
                    <CardTitle>Order History</CardTitle>
                    <CardDescription>All orders placed by this customer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingOrders ? (
                      <div className="text-center py-8">
                        <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
                        <p className="text-gray-500">Loading orders...</p>
                      </div>
                    ) : customerOrders.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No orders found for this customer</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="border-b">
                            <tr>
                              <th className="text-left py-2 px-2">Order Number</th>
                              <th className="text-left py-2 px-2">Date</th>
                              <th className="text-left py-2 px-2">Status</th>
                              <th className="text-right py-2 px-2">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerOrders.map((order) => (
                              <tr key={order.id} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-medium">{order.orderNumber}</td>
                                <td className="py-2 px-2">
                                  {new Date(order.createdAt).toLocaleDateString()}
                                </td>
                                <td className="py-2 px-2">
                                  <Badge variant="outline">{order.status}</Badge>
                                </td>
                                <td className="py-2 px-2 text-right font-semibold">
                                  ₹{(order.finalAmount || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="mt-4 pt-4 border-t">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-xs text-gray-500">Total Orders</div>
                              <div className="text-lg font-semibold">{customerOrders.length}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Total Revenue</div>
                              <div className="text-lg font-semibold">
                                ₹{customerOrders.reduce((sum, order) => sum + (order.finalAmount || 0), 0).toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Average Order Value</div>
                              <div className="text-lg font-semibold">
                                ₹{(customerOrders.length > 0
                                  ? customerOrders.reduce((sum, order) => sum + (order.finalAmount || 0), 0) / customerOrders.length
                                  : 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerManagement;